/* eslint-disable max-lines */
import { Box, Checkbox, LoadingOverlay } from '@mantine/core';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import Alert from '../Alert';
import LoadingBars from '../LoadingBars';
import styles from './styles.scoped.css';

function DataTable({
  className = '',
  column = [],
  data = [],
  endTreshould = 0,
  excludeColumLoading = [],
  isDraggable = false,
  isSelectable = false,
  loading = false,
  loadingRowIndex = [],
  meta = {},
  onColumnEdit = () => { },
  onDragLeave = () => { },
  onDragOver = () => { },
  onDragStart = () => { },
  onDrop = () => { },
  onEndReached = undefined,
  onRefresh = undefined,
  onSelectedRow = () => { },
  size = 0,
  striped = false,
  withIndex = false,
}) {
  const [alertVisible, setAlertVisible] = useState(false);

  const classes = [styles.root, ...(striped ? [styles.striped] : []), className]
    .filter(Boolean)
    .join(' ');

  const onScrolledToBottom = useDebouncedCallback(() => {
    onEndReached();
  }, 500);

  useEffect(() => {
    if (typeof onRefresh === 'function' && meta.loadedAt && meta.lastDataAt) {
      const loadedAt = moment(meta.loadedAt);
      const lastOutletDate = moment(meta.lastDataAt);
      const isNewDataAvailable = loadedAt.isBefore(lastOutletDate);
      if (isNewDataAvailable) {
        setAlertVisible(true);
      }
    }
  }, [meta]);

  useEffect(() => {
    const container = document.getElementById('children-container');

    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      const scroll = container.clientHeight + container.scrollTop;
      const height = container.scrollHeight - endTreshould;
      const scrollReached = scroll >= (height - 100) && scroll <= container.scrollHeight;

      if (
        !scrollReached ||
        loading ||
        data.length < 1 ||
        meta.page >= meta.totalPage
      ) {
        return;
      }

      if (typeof onEndReached === 'function') {
        onScrolledToBottom();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [
    data.length,
    endTreshould,
    loading,
    meta.page,
    meta.totalPage,
    onEndReached,
    onScrolledToBottom,
  ]);

  return (
    <Box pos="relative">
      {loading && (
        <LoadingOverlay loader={<></>} visible={loading} zIndex={99} />
      )}
      <table className={classes} data-column={'data'} id="data-table">
        <thead>
          <tr>
            {isSelectable && <th>Pilih</th>}
            {withIndex && (
              <th
                style={{
                  width: '12px',
                }}
              >
                No.
              </th>
            )}
            {column.map((item, idx) => (
              <TableHeader item={item} key={idx} />
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, idx) => (
            <TableRow
              column={column}
              excludeColumLoading={excludeColumLoading}
              from={typeof onEndReached === 'function' ? 1 : undefined}
              idx={idx}
              isDraggable={isDraggable}
              isSelectable={isSelectable}
              item={item}
              key={idx}
              loadingRowIndex={loadingRowIndex}
              meta={meta}
              onColumnEdit={onColumnEdit}
              onDragLeave={onDragLeave}
              onDragOver={onDragOver}
              onDragStart={onDragStart}
              onDrop={onDrop}
              onSelectedRow={onSelectedRow}
              size={size}
              withIndex={withIndex}
            />
          ))}
          {loading && (
            <tr>
              <td colSpan={column.length + (withIndex ? 1 : 0)}>
                <div className={styles['loading-bar']}>
                  <LoadingBars size={2.5} />
                </div>
              </td>
            </tr>
          )}
          {data.length === 0 && !loading && (
            <tr>
              <td
                className={styles['no-data']}
                colSpan={column.length + (withIndex ? 1 : 0)}
              >
                Tidak Ada Data
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <Alert
        confrimText="Refresh"
        content="Data Baru Tersedia"
        onClose={() => setAlertVisible(false)}
        onConfirm={() => {
          setAlertVisible(false);
          onRefresh();
        }}
        open={alertVisible}
      />
    </Box>
  );
}

DataTable.propTypes = {
  className: PropTypes.string,
  column: PropTypes.array,
  data: PropTypes.array,
  endTreshould: PropTypes.number,
  excludeColumLoading: PropTypes.arrayOf(PropTypes.string),
  isDraggable: PropTypes.bool,
  isSelectable: PropTypes.bool,
  loading: PropTypes.bool,
  loadingRowIndex: PropTypes.arrayOf(PropTypes.number),
  meta: PropTypes.object,
  onColumnEdit: PropTypes.func,
  onDragLeave: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragStart: PropTypes.func,
  onDrop: PropTypes.func,
  onEndReached: PropTypes.func,
  onRefresh: PropTypes.func,
  onSelectedRow: PropTypes.func,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  striped: PropTypes.bool,
  withIndex: PropTypes.bool,
};

export default DataTable;

export function TableHeader({ item }) {
  const className = [styles['table-header'], item.className].join(' ');
  return <th className={className}>{item.heading}</th>;
}

TableHeader.propTypes = {
  item: PropTypes.object,
};

export function TableRow({
  column = [],
  item = {},
  idx = 0,
  excludeColumLoading = [],
  isSelectable = false,
  loadingRowIndex = [],
  onColumnEdit = () => { },
  onSelectedRow = () => { },
  meta = {},
  size = 0,
  withIndex = false,
  from,
  isDraggable = false,
  onDrop = () => { },
  onDragOver = () => { },
  onDragLeave = () => { },
  onDragStart = () => { },
}) {
  let number = idx + 1;
  if (withIndex && meta.page > 1 && from === undefined) {
    from = size * meta.page - size + 1;
    number = idx + from;
  }
  const handleColoringRow = () => {
    if ((idx + 1) % 2 === 0) {
      return styles.lightBlue;
    } else {
      return styles.white;
    }
  };

  const custStyle = [
    handleColoringRow(),
    item.alert ? styles.alert : '',
    isDraggable ? styles.drag : '',
  ].join(' ');
  return (
    <tr
      className={custStyle}
      data-id={idx}
      draggable={isDraggable}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDragStart={onDragStart}
      onDrop={onDrop}
    >
      {isSelectable && (
        <td>
          <Checkbox
            checked={item.checked}
            onChange={() => onSelectedRow(item)}
          />
        </td>
      )}
      {withIndex && !loadingRowIndex.includes(idx) && <td>{number}.</td>}
      {withIndex && loadingRowIndex.includes(idx) && (
        <td>
          <div className={styles['td-loading']} />
        </td>
      )}
      {column.map((cItem, cIdx) => {
        const { key, value, className } = cItem;

        if (
          loadingRowIndex.includes(idx) &&
          !excludeColumLoading.includes(key)
        ) {
          return (
            <td className={className} key={cIdx}>
              <div className={styles['td-loading']} />
            </td>
          );
        }

        if (cItem.editable) {
          return (
            <td className={className} key={cIdx}>
              <input
                onChange={({ target }) => onColumnEdit(item, target.value)}
                type="text"
                value={item[value]}
              />
            </td>
          );
        }

        return (
          <td className={className} key={cIdx}>
            {typeof value === 'function'
              ? value(item, idx)
              : (item[value] ?? '-')}
          </td>
        );
      })}
    </tr>
  );
}

TableRow.propTypes = {
  column: PropTypes.array,
  excludeColumLoading: PropTypes.arrayOf(PropTypes.string),
  from: PropTypes.number,
  idx: PropTypes.number,
  isDraggable: PropTypes.bool,
  isSelectable: PropTypes.bool,
  item: PropTypes.object,
  loadingRowIndex: PropTypes.arrayOf(PropTypes.number),
  meta: PropTypes.object,
  onColumnEdit: PropTypes.func,
  onDragLeave: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragStart: PropTypes.func,
  onDrop: PropTypes.func,
  onSelectedRow: PropTypes.func,
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  withIndex: PropTypes.bool,
};
